import { StrictMode, Suspense, lazy, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App";
import { InstallPrompt } from "./components/InstallPrompt";
import { ReloadPrompt } from "./components/ReloadPrompt";
import { initTheme } from "./theme";
import { applyAppearancePrefs } from "./appearancePrefs";
import { captureInviteFromLocation } from "./messaging/pendingInvite";
import "./styles.css";
import "./parity.css";

initTheme();
applyAppearancePrefs();
// Invite links (`/app#i=…`, or `/#i=…`) go straight into the messenger; the
// invite is parked in sessionStorage and scrubbed from the URL + history.
captureInviteFromLocation();
// A newer Veto opened in another tab upgraded storage: reload into it (state is persisted).
window.addEventListener("veto:db-upgraded", () => window.location.reload());

// NFR-PERF-001: marketing locales (~48KB) must not bloat the /app cold start.
// Lazy-load so installed PWA / standalone only downloads the messenger shell.
const MarketingPage = lazy(() =>
  import("./marketing/MarketingPage").then((m) => ({ default: m.MarketingPage })),
);

function isStandaloneDisplay(): boolean {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia?.("(display-mode: standalone)")?.matches;
  const ios = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return Boolean(mq || ios);
}

function isAppPath(pathname: string): boolean {
  return pathname === "/app" || pathname.startsWith("/app/");
}

function Root() {
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Mobile keyboard (SPEC §6.2): when visualViewport shrinks >25% (iOS Safari /
  // Android WebView ignoring interactive-widget), collapse secondary chrome so
  // the composer + focused input stay visible. Pure CSS fallback already covers
  // modern Chromium via interactive-widget=resizes-content.
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const open = vv.height < window.innerHeight * 0.75;
        document.body.classList.toggle("keyboard-open", open);
      });
    };
    vv.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      vv.removeEventListener("resize", onResize);
      document.body.classList.remove("keyboard-open");
    };
  }, []);

  const enterApp = () => {
    const url = new URL(window.location.href);
    // Preserve deep links (FR-CON-001): share_target ?text=veto-invite:… / ?addContact=1 / lang.
    const keep = new URLSearchParams();
    for (const k of ["lang", "addContact", "add", "invite", "text", "title", "url"]) {
      const v = url.searchParams.get(k);
      if (v) keep.set(k, v);
    }
    const qs = keep.toString();
    const next = `/app${qs ? `?${qs}` : ""}`;
    window.history.pushState({}, "", next);
    setPath("/app");
    window.scrollTo(0, 0);
  };

  // Installed PWA / standalone → messenger. Marketing stays at `/`.
  const showApp = isAppPath(path) || isStandaloneDisplay();

  // NFR-PWA-001/004: install + update prompts must work from the landing page too —
  // most Android installs start at `/`, not `/app`.
  if (!showApp) {
    return (
      <>
        <Suspense fallback={<div className="loading-screen"><div className="loading-mark">V</div></div>}>
          <MarketingPage onEnterApp={enterApp} />
        </Suspense>
        <InstallPrompt />
        <ReloadPrompt />
      </>
    );
  }

  return (
    <>
      <App />
      <InstallPrompt />
      <ReloadPrompt />
    </>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
